import React, { useEffect, useState } from "react";
import { CheckCircle2, ArrowRight, Loader2 } from "lucide-react";
import { markSubscribed } from "../lib/subscription";
import { analytics } from "@heycatch/sdk";

const API = process.env.REACT_APP_BACKEND_URL || "";

/**
 * StripeSuccessAutoActivate — mounted at the root of Landing.
 *
 * When Stripe redirects the user back to `/?trial=success&session_id=…`
 * (or `/?trial=cancel`), this component takes over the page with a
 * lightweight overlay:
 *   · Polls `/api/subscription/status/{session_id}` until the record is
 *     `trialing`/`active` (the webhook writes it moments after the card
 *     is captured — usually within 500 ms).
 *   · Once confirmed, flips localStorage subscription state to `active`
 *     and reveals a "Open EVIQ" button.
 *
 * If the user cancels checkout (`?trial=cancel`), just shows a soft
 * "checkout not completed" nudge and lets them retry.
 */
export const StripeSuccessAutoActivate = () => {
  const params = new URLSearchParams(window.location.search);
  const trial = params.get("trial"); // "success" | "cancel" | null
  const querySessionId = params.get("session_id");
  const storedSessionId =
    typeof window !== "undefined"
      ? window.localStorage.getItem("eviq.pending_stripe_session_id")
      : null;
  const storedPlanId =
    typeof window !== "undefined"
      ? window.localStorage.getItem("eviq.pending_stripe_plan_id")
      : null;

  const sessionId = querySessionId || storedSessionId;

  const [state, setState] = useState("polling"); // "polling" | "activated" | "canceled" | "failed"
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (trial === "cancel") {
      setState("canceled");
      return;
    }
    if (trial !== "success" || !sessionId) return;

    let cancelled = false;

    const poll = async () => {
      try {
        const res = await fetch(`${API}/api/subscription/status/${sessionId}`);
        if (!res.ok) throw new Error("status_error");
        const data = await res.json();
        const s = data.subscription_status;
        if (s === "trialing" || s === "active") {
          if (cancelled) return;
          const planId = storedPlanId || data.plan_id || "yearly";
          markSubscribed("active", planId);
          analytics.trackEvent("subscription_started", { plan: planId });
          try {
            window.localStorage.setItem("eviq.subscription_email", data.email || "");
            window.localStorage.removeItem("eviq.pending_stripe_session_id");
            window.localStorage.removeItem("eviq.pending_stripe_plan_id");
            window.localStorage.removeItem("eviq.pending_stripe_email");
          } catch { /* ignore */ }
          // Clean the URL so refresh doesn't re-trigger the flow.
          try {
            const url = new URL(window.location.href);
            url.searchParams.delete("trial");
            url.searchParams.delete("session_id");
            window.history.replaceState({}, "", url.toString());
          } catch { /* ignore */ }
          setState("activated");
          return;
        }
      } catch { /* fall through to retry */ }
      setAttempts((n) => n + 1);
    };

    // Poll every 1s for up to ~12 attempts (webhook nearly always lands
    // within 500ms; 12s is a generous outer bound).
    poll();
    const id = setInterval(() => {
      if (cancelled) return;
      poll();
    }, 1000);
    const kill = setTimeout(() => {
      if (state === "polling") setState("failed");
      clearInterval(id);
    }, 12_000);

    return () => {
      cancelled = true;
      clearInterval(id);
      clearTimeout(kill);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trial, sessionId]);

  if (trial !== "success" && trial !== "cancel") return null;

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center bg-black/90 backdrop-blur-md px-5"
      data-testid="stripe-success-overlay"
    >
      <div className="w-full max-w-md rounded-2xl border border-white/10 bg-[#0a0a0a] p-8 text-center shadow-2xl">
        {state === "polling" && (
          <>
            <Loader2
              className="w-10 h-10 animate-spin mx-auto mb-4"
              style={{ color: "#39FF14" }}
              strokeWidth={2.2}
            />
            <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-zinc-500 mb-2">
              Confirming with Stripe
            </div>
            <h2 className="font-display text-2xl text-white font-semibold mb-2">
              Setting up your account…
            </h2>
            <p className="text-sm text-zinc-400 leading-relaxed">
              Your card was captured — <span className="text-white">$0.00 today</span>.
              We&#39;re verifying the payment and unlocking EVIQ for you.
            </p>
            {attempts > 3 && (
              <p className="text-[11px] text-zinc-500 mt-3">
                Stripe is taking a moment — hang tight.
              </p>
            )}
          </>
        )}

        {state === "activated" && (
          <>
            <div
              className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4"
              style={{ background: "rgba(57,255,20,0.14)", border: "2px solid rgba(57,255,20,0.45)" }}
            >
              <CheckCircle2 className="w-7 h-7" style={{ color: "#39FF14" }} strokeWidth={2.5} />
            </div>
            <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-zinc-500 mb-2">
              Trial started
            </div>
            <h2 className="font-display text-2xl text-white font-semibold mb-3">
              Welcome to EVIQ.
            </h2>
            <p className="text-sm text-zinc-400 leading-relaxed">
              Your 7-day free trial is active. First charge is 7 days from now — cancel
              anytime before then and you won&#39;t pay a cent.
            </p>
            <a
              href="/app"
              data-testid="stripe-success-open-app"
              className="mt-6 brand-gradient text-black font-semibold rounded-full px-6 py-3 text-sm inline-flex items-center justify-center gap-2 hover:opacity-90 transition-opacity"
            >
              Open EVIQ
              <ArrowRight className="w-4 h-4" strokeWidth={2.5} />
            </a>
          </>
        )}

        {state === "canceled" && (
          <>
            <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-zinc-500 mb-2">
              Checkout not completed
            </div>
            <h2 className="font-display text-2xl text-white font-semibold mb-3">
              No card was charged.
            </h2>
            <p className="text-sm text-zinc-400 leading-relaxed">
              You closed the Stripe tab before finishing. When you&#39;re ready, come
              back to pricing and start the free trial again.
            </p>
            <a
              href="/#pricing"
              onClick={() => {
                try { window.history.replaceState({}, "", "/"); } catch { /* ignore */ }
              }}
              data-testid="stripe-canceled-back"
              className="mt-6 inline-flex items-center gap-2 rounded-full border border-white/15 px-5 py-2.5 text-sm text-white hover:bg-white/[0.04]"
            >
              Back to pricing
              <ArrowRight className="w-4 h-4" strokeWidth={2.5} />
            </a>
          </>
        )}

        {state === "failed" && (
          <>
            <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-zinc-500 mb-2">
              Verification pending
            </div>
            <h2 className="font-display text-2xl text-white font-semibold mb-3">
              Card captured — hold tight.
            </h2>
            <p className="text-sm text-zinc-400 leading-relaxed">
              Stripe confirmed the checkout, but the webhook hasn&#39;t landed yet.
              Refresh in a moment and your account will be unlocked automatically.
            </p>
            <button
              type="button"
              onClick={() => window.location.reload()}
              data-testid="stripe-retry-poll"
              className="mt-6 brand-gradient text-black font-semibold rounded-full px-5 py-2.5 text-sm inline-flex items-center justify-center gap-2 hover:opacity-90 transition-opacity"
            >
              Refresh
            </button>
          </>
        )}
      </div>
    </div>
  );
};
